import { createContext, useContext, useState, ReactNode } from 'react';
import { useTranscribe } from '../hooks/useTranscribe';
import { useToneAnalysis, ToneProfile } from '../hooks/useToneAnalysis';
import { useContentGeneration, ContentGenerationRequest, GeneratedContent } from '../hooks/useContentGeneration';

type ApiContextType = {
  // Data states
  transcript: string;
  setTranscript: (t: string) => void;
  toneProfile: ToneProfile | null;
  setToneProfile: (p: ToneProfile | null) => void;
  generatedContent: GeneratedContent | null;
  setGeneratedContent: (c: GeneratedContent | null) => void;
  
  // API functions
  transcribeYouTube: (url: string) => Promise<string>;
  analyzeTone: (samples: string[]) => Promise<ToneProfile>;
  generateContent: (request: ContentGenerationRequest) => Promise<GeneratedContent>;
  
  // Loading states
  isTranscribing: boolean;
  isAnalyzingTone: boolean;
  isGeneratingContent: boolean;
  
  // Error states
  transcribeError: string | null;
  toneError: string | null;
  generationError: string | null;
};

const ApiContext = createContext<ApiContextType>(null!);

export const ApiProvider = ({ children }: { children: ReactNode }) => {
  // Data states
  const [transcript, setTranscript] = useState('');
  const [toneProfile, setToneProfile] = useState<ToneProfile | null>(null);
  const [generatedContent, setGeneratedContent] = useState<GeneratedContent | null>(null);
  
  // API hooks
  const { transcribe, isLoading: isTranscribing, error: transcribeError } = useTranscribe();
  const { analyzeSamples, isLoading: isAnalyzingTone, error: toneError } = useToneAnalysis();
  const { generatePosts, isLoading: isGeneratingContent, error: generationError } = useContentGeneration();

  return (
    <ApiContext.Provider value={{
      // Data states
      transcript, 
      setTranscript,
      toneProfile, 
      setToneProfile,
      generatedContent, 
      setGeneratedContent,
      
      // API functions
      transcribeYouTube: transcribe,
      analyzeTone: analyzeSamples,
      generateContent: generatePosts,
      
      // Loading states
      isTranscribing,
      isAnalyzingTone,
      isGeneratingContent,
      
      // Error states
      transcribeError,
      toneError,
      generationError
    }}>
      {children}
    </ApiContext.Provider>
  );
};

export const useApiContext = () => useContext(ApiContext);